import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  Badge,
  Button,
  confirm,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  FormInput,
  FormPrice,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import {
  CheckIcon,
  HandCoinsIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import type { CommissionDTO } from "../../domain/contracts";
import { performanceApi } from "../api/client";
import {
  useCancelCommission,
  useCreateCommission,
  useDeleteCommission,
  useEventCommissionsQuery,
  useMarkCommissionPaid,
  useUpdateCommission,
} from "../api/queries";
import { PerformanceBrandPrintMenu } from "./PerformanceBrandPrintMenu";

const CommissionFormSchema = z.object({
  payeeName: z.string().trim().min(1, "ຕ້ອງປ້ອນຊື່ຜູ້ຮັບ"),
  payeePhone: z.string().optional(),
  amount: z.number().int().positive("ຍອດຕ້ອງຫຼາຍກວ່າ 0"),
  notes: z.string().optional(),
  markPaid: z.boolean(),
});

type CommissionFormValues = z.infer<typeof CommissionFormSchema>;

const STATUS_LABELS = {
  pending: "ລໍຖ້າຈ່າຍ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS = {
  pending: "secondary",
  paid: "default",
  cancelled: "destructive",
} as const;

function formatMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

type CommissionsSectionProps = {
  eventId: string;
};

type CommissionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  editing: CommissionDTO | null;
};

function CommissionDialog({
  open,
  onOpenChange,
  eventId,
  editing,
}: CommissionDialogProps) {
  const createCommission = useCreateCommission(eventId);
  const updateCommission = useUpdateCommission(eventId);
  const isEdit = Boolean(editing);

  const methods = RHF.useForm<CommissionFormValues>({
    resolver: zodResolver(CommissionFormSchema),
    defaultValues: {
      payeeName: "",
      payeePhone: "",
      amount: 0,
      notes: "",
      markPaid: false,
    },
  });

  useEffect(() => {
    if (!open) return;
    methods.reset({
      payeeName: editing?.payeeName ?? "",
      payeePhone: editing?.payeePhone ?? "",
      amount: editing?.amount ?? 0,
      notes: editing?.notes ?? "",
      markPaid: false,
    });
  }, [open, editing, methods]);

  const pending =
    createCommission.isPending || updateCommission.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "ແກ້ໄຂຄ່ານາຍໜ້າ" : "ເພີ່ມຄ່ານາຍໜ້າ"}
          </DialogTitle>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={(values) => {
            if (isEdit && editing) {
              updateCommission.mutate(
                {
                  commissionId: editing.id,
                  input: {
                    payeeName: values.payeeName,
                    payeePhone: values.payeePhone?.trim() || null,
                    amount: values.amount,
                    notes: values.notes?.trim() || null,
                  },
                },
                { onSuccess: () => onOpenChange(false) },
              );
              return;
            }
            createCommission.mutate(
              {
                payeeName: values.payeeName,
                payeePhone: values.payeePhone?.trim() || null,
                amount: values.amount,
                notes: values.notes?.trim() || null,
                markPaid: values.markPaid,
              },
              { onSuccess: () => onOpenChange(false) },
            );
          }}
          className="space-y-4"
        >
          <FormInput name="payeeName" label="ຊື່ຜູ້ຮັບ" />
          <FormInput name="payeePhone" label="ເບີໂທ (ຖ້າມີ)" />
          <FormPrice
            name="amount"
            currencySymbol="₭"
            label="ຍອດຄ່ານາຍໜ້າ"
            min={1}
          />
          <p className="-mt-2 text-muted-foreground text-xs">
            ປ້ອນຕົວເລກເອງ — ບໍ່ຄິດ % ອັດຕະໂນມັດ
          </p>
          <FormTextarea name="notes" label="ໝາຍເຫດ" rows={2} />
          {!isEdit ? (
            <FormCheckboxCard
              name="markPaid"
              label="ບັນທຶກວ່າຈ່າຍແລ້ວທັນທີ"
              description="ເປີດໄດ້ຖ້າຈ່າຍແລ້ວຕອນເພີ່ມລາຍການ"
            />
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}

export function CommissionsSection({ eventId }: CommissionsSectionProps) {
  const canCreate = useActionPermission(["performance-payouts:create"]);
  const canMarkPaid = useActionPermission(["performance-payouts:mark-paid"]);
  const canDelete = useActionPermission(["performance-payouts:delete"]);
  const query = useEventCommissionsQuery(eventId);
  const markPaid = useMarkCommissionPaid(eventId);
  const cancelCommission = useCancelCommission(eventId);
  const deleteCommission = useDeleteCommission(eventId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionDTO | null>(null);

  const items = query.data ?? [];
  const activeTotal = items
    .filter((row) => row.status !== "cancelled")
    .reduce((sum, row) => sum + row.amount, 0);
  const paidTotal = items
    .filter((row) => row.status === "paid")
    .reduce((sum, row) => sum + row.amount, 0);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <HandCoinsIcon className="size-4 text-primary" />
            <h2 className="font-semibold text-sm uppercase tracking-widest">
              ຄ່ານາຍໜ້າ
            </h2>
          </div>
          <p className="mt-1 text-muted-foreground text-xs">
            ປ້ອນຍອດເອງ · ຈ່າຍໄດ້ທຸກເວລາ · ບໍ່ຜູກກັບ QT/INV
          </p>
        </div>
        {canCreate ? (
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <PlusIcon className="size-4" />
            ເພີ່ມຄ່ານາຍໜ້າ
          </Button>
        ) : null}
      </div>

      <div className="flex-1 space-y-3 p-5">
        {items.length > 0 ? (
          <div className="flex flex-wrap gap-3 text-sm">
            <div className="rounded-md border px-3 py-2">
              <p className="text-muted-foreground text-xs">ລວມທັງໝົດ</p>
              <p className="font-semibold tabular-nums">
                {formatMoney(activeTotal)}
              </p>
            </div>
            <div className="rounded-md border px-3 py-2">
              <p className="text-muted-foreground text-xs">ຈ່າຍແລ້ວ</p>
              <p className="font-semibold tabular-nums">
                {formatMoney(paidTotal)}
              </p>
            </div>
          </div>
        ) : null}

        {query.isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : items.length === 0 ? (
          <Empty className="border border-dashed py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <HandCoinsIcon />
              </EmptyMedia>
              <EmptyTitle>ຍັງບໍ່ມີຄ່ານາຍໜ້າ</EmptyTitle>
              <EmptyDescription>
                ເພີ່ມລາຍການເມື່ອມີການຈ່າຍຄ່ານາຍໜ້າສຳລັບງານນີ້
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ul className="space-y-2">
            {items.map((row) => {
              const canEditRow = canCreate && row.status === "pending";
              const canPayRow = canMarkPaid && row.status === "pending";
              const canCancelRow = canCreate && row.status === "pending";
              const canDeleteRow =
                canDelete &&
                (row.status === "pending" || row.status === "cancelled");

              return (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-sm">{row.payeeName}</p>
                      <Badge variant={STATUS_VARIANTS[row.status]}>
                        {STATUS_LABELS[row.status]}
                      </Badge>
                    </div>
                    <p className="mt-0.5 font-semibold text-sm tabular-nums">
                      {formatMoney(row.amount)}
                    </p>
                    {row.payeePhone ? (
                      <p className="text-muted-foreground text-xs">
                        {row.payeePhone}
                      </p>
                    ) : null}
                    {row.notes ? (
                      <p className="mt-1 text-muted-foreground text-xs">
                        {row.notes}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {row.status === "paid" ? (
                      <PerformanceBrandPrintMenu
                        className="h-8 gap-1 px-2"
                        buildPrintUrl={(lang) =>
                          performanceApi.events.commissions.printUrl(
                            eventId,
                            row.id,
                            lang,
                          )
                        }
                      />
                    ) : null}
                    {canEditRow ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditing(row);
                          setDialogOpen(true);
                        }}
                      >
                        <PencilIcon className="size-4" />
                        ແກ້ໄຂ
                      </Button>
                    ) : null}
                    {canPayRow ? (
                      <Button
                        type="button"
                        size="sm"
                        disabled={markPaid.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ບັນທຶກຈ່າຍຄ່ານາຍໜ້າ",
                            description: `ຢືນຢັນວ່າຈ່າຍ ${formatMoney(row.amount)} ໃຫ້ ${row.payeeName} ແລ້ວ?`,
                            actionText: "ຈ່າຍແລ້ວ",
                          });
                          if (ok) markPaid.mutate(row.id);
                        }}
                      >
                        <CheckIcon className="size-4" />
                        ຈ່າຍແລ້ວ
                      </Button>
                    ) : null}
                    {canCancelRow ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={cancelCommission.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ຍົກເລີກຄ່ານາຍໜ້າ",
                            description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກລາຍການນີ້?",
                            actionText: "ຍົກເລີກ",
                            ActionProps: { variant: "destructive" },
                          });
                          if (ok) cancelCommission.mutate(row.id);
                        }}
                      >
                        <XIcon className="size-4" />
                        ຍົກເລີກ
                      </Button>
                    ) : null}
                    {canDeleteRow ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={deleteCommission.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ລຶບຄ່ານາຍໜ້າ",
                            description: "ລຶບລາຍການນີ້ອອກຖາວອນ?",
                            actionText: "ລຶບ",
                            ActionProps: { variant: "destructive" },
                          });
                          if (ok) deleteCommission.mutate(row.id);
                        }}
                      >
                        <Trash2Icon className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <CommissionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        eventId={eventId}
        editing={editing}
      />
    </div>
  );
}
